import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { CityDetailResults } from '@/types/marketplace';
import { CityListItem } from '@/features/marketplace/components/city-list-item';

interface CitiesGridProps {
  cities: CityDetailResults[];
  onCityClick: (city: CityDetailResults) => void;
  title?: string;
  className?: string;
  showAllCities?: boolean;
  onAllCitiesClick?: () => void;
}

const CitiesGrid: React.FC<CitiesGridProps> = ({
  cities,
  onCityClick,
  title,
  className = '',
  showAllCities = false,
  onAllCitiesClick,
}) => {
  const { t } = useTranslation();

  const hasImmediateAvailability = useMemo(
    () => cities.some((city) => city.available_immediately_count > 0),
    [cities]
  );

  const renderCityButton = (city: CityDetailResults) => {
    return (
      <button
        key={city.id}
        type="button"
        onClick={() => onCityClick(city)}
        style={{
          background: '#f9f9f9',
          border: '1px solid #eee',
        }}
        className="gap-12 flex w-full items-center rounded-lg p-2 hover:border-[1.5px] hover:border-border-primaryHeaviest"
      >
        <CityListItem
          city={city}
          imgSize={48}
          nameClassName="text-[14px] font-bold leading-[16px] text-text-primaryHeavy"
          availableSoonClassName="text-text-primaryMedium"
          noAvailableRoomsClassName="text-text-primaryMedium"
        />
      </button>
    );
  };

  const renderAllCitiesButton = () => (
    <button
      key="all-cities"
      type="button"
      onClick={onAllCitiesClick}
      style={{
        background: '#f9f9f9',
        border: '1px solid #eee',
      }}
      className="gap-12 flex w-full items-center rounded-lg p-2 hover:border-[1.5px] hover:border-border-primaryHeaviest"
    >
      <div className="relative ml-2 min-w-0 flex-1 text-left">
        <div className="flex w-full items-center justify-between">
          <span className="text-[14px] font-bold leading-[16px] text-text-primaryHeavy">
            {t('js.components.all_cities_dropdown.all_cities')}
          </span>
        </div>
        <span className="text-[12px] font-medium text-[#1B998B]">
          {t('js.components.all_cities_dropdown.take_me_everywhere')}
        </span>

        {hasImmediateAvailability && (
          <span className="absolute right-0 top-1/2 -translate-y-1/2">
            <ReactSVG
              src={require(`@assets/images/marketplace/lighting_active.svg`)}
              className="searchbar-item-available-immediately"
              aria-hidden="true"
              beforeInjection={(svg) => {
                svg.setAttribute('width', `18`);
                svg.setAttribute('height', `18`);
                svg.setAttribute('aria-hidden', 'true');
              }}
            />
          </span>
        )}
      </div>
    </button>
  );

  return (
    <div className={className}>
      {title && <h3 className="mb-4 text-[18px] font-bold text-N900">{title}</h3>}
      <div className="grid grid-cols-4 gap-3">
        {showAllCities && renderAllCitiesButton()}
        {cities.map((city) => renderCityButton(city))}
      </div>
    </div>
  );
};

export { CitiesGrid };
